import React, { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'

const BOUNDARY_VISIBLE_MS = 2000

export function TimeboxBlock({
  id,
  label,
  color,
  lengthSlots,
  startSlot,
  typeId,
  top,
  height,
  isDragging,
  onDragStart,
  onDragMove,
  onDragEnd,
  onSplit,
  onMoveUp,
  onMoveDown,
  isLifted,
  locked,
}) {
  const blockRef = useRef(null)
  const menuButtonRef = useRef(null)
  const menuPanelRef = useRef(null)
  const activeRef = useRef(false)
  const boundaryTimeoutRef = useRef(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [menuPosition, setMenuPosition] = useState(null)
  const [visibleBoundaryIndex, setVisibleBoundaryIndex] = useState(null)

  // Overlay receives pointerup, not the block — clear activeRef when drag ends from parent state
  useEffect(() => {
    if (!isDragging && !isLifted) {
      activeRef.current = false
    }
  }, [isDragging, isLifted])

  const canDrag = typeof onDragStart === 'function'

  const handlePointerDown = useCallback(
    e => {
      if (!canDrag) return
      e.preventDefault()
      activeRef.current = true
      blockRef.current?.setPointerCapture?.(e.pointerId)
      onDragStart(e.clientX, e.clientY)
    },
    [onDragStart, canDrag]
  )

  const handlePointerMove = useCallback(
    e => {
      if (!activeRef.current) return
      e.preventDefault()
      if (onDragMove) onDragMove(e.clientX, e.clientY)
    },
    [onDragMove]
  )

  const handlePointerUpOrCancel = useCallback(() => {
    if (!activeRef.current) return
    activeRef.current = false
    if (onDragEnd) onDragEnd()
  }, [onDragEnd])

  // Fallback: overlay may not be in DOM yet on first pointermove; window listeners catch it.
  useEffect(() => {
    if (!activeRef.current || isLifted || !isDragging) return
    const move = e => handlePointerMove(e)
    const up = () => handlePointerUpOrCancel()
    window.addEventListener('pointermove', move, { passive: false })
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
    return () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
    }
  }, [handlePointerMove, handlePointerUpOrCancel, isLifted, isDragging])

  const handleMenuButtonPointerDown = useCallback(e => {
    e.stopPropagation()
    e.preventDefault()
  }, [])

  const handleMenuButtonClick = useCallback(e => {
    e.stopPropagation()
    setMenuOpen(prev => !prev)
  }, [])

  const handleBoundaryEnter = useCallback((boundaryIndex) => {
    if (boundaryTimeoutRef.current) {
      clearTimeout(boundaryTimeoutRef.current)
      boundaryTimeoutRef.current = null
    }
    setVisibleBoundaryIndex(boundaryIndex)
    boundaryTimeoutRef.current = setTimeout(() => {
      setVisibleBoundaryIndex(null)
      boundaryTimeoutRef.current = null
    }, BOUNDARY_VISIBLE_MS)
  }, [])

  const handleBoundaryLeave = useCallback(() => {
    if (boundaryTimeoutRef.current) {
      clearTimeout(boundaryTimeoutRef.current)
      boundaryTimeoutRef.current = null
    }
    setVisibleBoundaryIndex(null)
  }, [])

  useEffect(() => {
    return () => {
      if (boundaryTimeoutRef.current) clearTimeout(boundaryTimeoutRef.current)
    }
  }, [])

  const handleSplitClick = useCallback((e, splitAfterSlot) => {
    e.stopPropagation()
    e.preventDefault()
    onSplit?.(splitAfterSlot)
  }, [onSplit])

  // When menu opens, set position from button rect (after portal is in DOM)
  useEffect(() => {
    if (!menuOpen || !menuButtonRef.current || typeof window === 'undefined') return
    const menuWidth = 120
    const padding = 8
    const rect = menuButtonRef.current.getBoundingClientRect()
    let left = rect.right - menuWidth
    if (left < padding) left = padding
    if (left + menuWidth > window.innerWidth - padding) left = window.innerWidth - menuWidth - padding
    let top = rect.bottom + 4
    if (top + 80 > window.innerHeight - padding) top = Math.max(padding, rect.top - 84)
    if (top < padding) top = padding
    setMenuPosition({ top, left })
  }, [menuOpen])

  useEffect(() => {
    if (!menuOpen) return
    const close = e => {
      if (
        menuPanelRef.current?.contains(e.target) ||
        menuButtonRef.current?.contains(e.target)
      ) return
      setMenuOpen(false)
      setMenuPosition(null)
    }
    const onKey = e => {
      if (e.key === 'Escape') {
        setMenuOpen(false)
        setMenuPosition(null)
      }
    }
    document.addEventListener('pointerdown', close)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', close)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen])

  const canSplit = !locked && lengthSlots > 1 && typeId !== 'silent-day' && typeof onSplit === 'function'
  const slotHeight = height / lengthSlots

  return (
    <div
      ref={blockRef}
      data-timebox-id={id}
      className={`timebox-block ${lengthSlots > 1 ? 'timebox-block-composite' : ''} ${isDragging ? 'dragging' : ''} ${isLifted ? 'timebox-block-lifted' : ''}`}
      style={{
        top: `${top}px`,
        height: `${height}px`,
        '--block-color': color,
      }}
      onPointerDown={canDrag ? handlePointerDown : undefined}
    >
      {canSplit && Array.from({ length: lengthSlots - 1 }, (_, i) => {
        const boundaryIndex = i + 1
        const splitAfterSlot = (startSlot ?? 0) + boundaryIndex - 1
        const boundaryTop = boundaryIndex * slotHeight
        const isVisible = visibleBoundaryIndex === boundaryIndex
        return (
          <div
            key={boundaryIndex}
            className={`timebox-split-boundary ${isVisible ? 'timebox-split-boundary-visible' : ''}`}
            style={{ top: boundaryTop }}
            onPointerEnter={() => handleBoundaryEnter(boundaryIndex)}
            onPointerLeave={handleBoundaryLeave}
          >
            <button
              type="button"
              className="timebox-split-btn"
              aria-label={`Split after ${boundaryIndex} timebox`}
              onClick={e => handleSplitClick(e, splitAfterSlot)}
              onPointerDown={e => { e.stopPropagation(); e.preventDefault() }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M20 4L8.5 15.5"/><path d="M8.5 8.5L20 20"/>
              </svg>
            </button>
          </div>
        )
      })}
      <div className="timebox-block-inner">
        <div className="timebox-block-label-wrap">
          <span className="timebox-label">{label}</span>
          {lengthSlots > 1 && (
            <span className="timebox-duration">{lengthSlots} timeboxes</span>
          )}
        </div>
        <div className="timebox-block-actions">
          <div className="timebox-block-arrows">
            {onMoveUp && (
              <button
                type="button"
                className="timebox-move-btn timebox-move-up"
                aria-label="Move block up"
                onClick={e => { e.stopPropagation(); onMoveUp() }}
                onPointerDown={e => e.stopPropagation()}
              >
                ↑
              </button>
            )}
            {onMoveDown && (
              <button
                type="button"
                className="timebox-move-btn timebox-move-down"
                aria-label="Move block down"
                onClick={e => { e.stopPropagation(); onMoveDown() }}
                onPointerDown={e => e.stopPropagation()}
              >
                ↓
              </button>
            )}
          </div>
          <button
            ref={menuButtonRef}
            type="button"
            className="timebox-block-menu-trigger"
            aria-label="Block menu"
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            onPointerDown={handleMenuButtonPointerDown}
            onClick={handleMenuButtonClick}
          >
            ⋮
          </button>
        </div>
      </div>
      {menuOpen && typeof document !== 'undefined' && document.body && menuPosition &&
        createPortal(
          <div
            ref={menuPanelRef}
            className="timebox-block-menu timebox-block-menu-portal"
            role="menu"
            aria-label="Block actions"
            style={{
              position: 'fixed',
              top: menuPosition.top,
              left: menuPosition.left,
              width: 120,
            }}
          >
            <span className="timebox-block-menu-placeholder">Actions (soon)</span>
          </div>,
          document.body
        )}
    </div>
  )
}
